import type { TransferAssetOwnershipInput } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { useToast } from '../../lib/use-toast';
import { listTrailerOwnershipPeriods, transferTrailerOwnership, type TrailerOwnershipPeriod } from './trailers.api';

export type TrailerOwnershipState =
  { status: 'loading' } | { status: 'loaded'; periods: TrailerOwnershipPeriod[] } | { status: 'forbidden' } | { status: 'error' };

function toErrorState(err: unknown): TrailerOwnershipState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The Ownership tab's periods and its one mutation, transfer — reloaded after a successful transfer, same shape as `useTruckOwnership`. */
export function useTrailerOwnership(trailerId: string, trailerRegistration: string) {
  const [state, setState] = useState<TrailerOwnershipState>({ status: 'loading' });
  const [transferring, setTransferring] = useState(false);
  const toast = useToast();

  async function load() {
    try {
      setState({ status: 'loaded', periods: await listTrailerOwnershipPeriods(trailerId) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  useEffect(() => {
    let cancelled = false;
    listTrailerOwnershipPeriods(trailerId)
      .then((periods) => {
        if (!cancelled) setState({ status: 'loaded', periods });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });
    return () => {
      cancelled = true;
    };
  }, [trailerId]);

  async function transfer(input: TransferAssetOwnershipInput, ownerName: string): Promise<boolean> {
    setTransferring(true);
    try {
      await transferTrailerOwnership(trailerId, input);
      await load();
      toast.success(`Trailer ${trailerRegistration} ownership transferred to ${ownerName}`);
      return true;
    } catch (err) {
      toast.error(err instanceof ApiError && err.status === 409 ? 'The new period must start after the current one began' : "Couldn't transfer ownership");
      return false;
    } finally {
      setTransferring(false);
    }
  }

  return { state, transferring, transfer };
}
